import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GraduationCap, ArrowRight, ExternalLink, ShieldCheck, Users,
  BookOpen, Award, Bike, Target, CheckCircle2, Info, School,
  Accessibility, Clock, FileText, Lightbulb, MapPin,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import useSeo from '../hooks/useSeo';
import { buildBreadcrumb } from '../seo/breadcrumbs';

/**
 * Page Hub ASSR — Attestations scolaires de sécurité routière.
 * Objectif pédagogique : expliquer ce que sont les 4 attestations officielles
 * (ASSR1, ASSR2, ASR, AER), à qui elles s'adressent, et comment elles
 * s'articulent avec le parcours du permis moto / BSR / AM.
 *
 * Les CTAs d'entraînement renvoient vers le site OFFICIEL du Ministère
 * de l'Éducation nationale (e-assr.education-securite-routiere.fr) qui
 * propose gratuitement la préparation officielle. On ne duplique pas
 * le contenu — notre valeur ajoutée est la contextualisation et la
 * mise en perspective avec le parcours moto.
 */

// ---------- Données des 4 attestations ----------
const ATTESTATIONS = [
  {
    code: 'ASSR 1',
    fullName: 'Attestation Scolaire de Sécurité Routière — niveau 1',
    grade: '5ᵉ',
    age: '≈ 13 ans',
    color: '#0ea5e9', // bleu
    icon: School,
    audience: 'Élèves en classe de 5ᵉ',
    prereq: 'Validée en milieu scolaire',
    nextStep: 'Indispensable pour passer le BSR / AM (scooter 50 cm³, quadricycle léger)',
    description:
      "Première étape officielle de l'éducation à la sécurité routière. Évalue les connaissances de base : piéton, passager, cycliste, premiers secours. Obtenue en fin de 5ᵉ.",
    topics: [
      'Le piéton : traverser en sécurité',
      'Le passager : ceinture, siège auto, comportement',
      'Le cycliste : équipement, priorités, signalisation',
      'Les premiers secours (PAS : Protéger, Alerter, Secourir)',
    ],
    motoLink: {
      icon: Bike,
      label: 'Lien avec le permis AM (ex-BSR)',
      text: "Pour conduire un scooter 50 cm³ dès 14 ans, il faut ASSR1 + une formation pratique de 8 h en moto-école.",
      to: '/parcours',
      toLabel: 'Voir le parcours AM',
    },
    guideArticle: {
      to: '/magazine/assr1-preparer-5eme',
      label: 'Lire notre guide complet ASSR 1',
    },
    officialUrl: 'https://e-assr.education-securite-routiere.fr/preparer/assr/1/ASSR1',
  },
  {
    code: 'ASSR 2',
    fullName: 'Attestation Scolaire de Sécurité Routière — niveau 2',
    grade: '3ᵉ',
    age: '≈ 15 ans',
    color: '#16a34a', // vert
    icon: GraduationCap,
    audience: 'Élèves en classe de 3ᵉ',
    prereq: "Ne nécessite pas d'avoir validé l'ASSR1 au préalable",
    nextStep: "Obligatoire pour s'inscrire à l'examen du permis (B, A1, A2…) pour les jeunes nés après 1988",
    description:
      "Deuxième niveau : approfondit les situations routières, la responsabilité, le partage de la route avec d'autres usagers. Obtenue en fin de 3ᵉ. Elle reste valable toute la vie.",
    topics: [
      'Le partage de la route (piétons, vélos, motos, voitures)',
      'Les règles de priorité et la signalisation',
      "L'alcool, les stupéfiants, la fatigue au volant",
      'La responsabilité du conducteur (civile et pénale)',
      "L'assurance et le comportement face à un accident",
    ],
    motoLink: {
      icon: Bike,
      label: 'Lien avec le permis moto',
      text: "ASSR2 est exigée pour passer le Code moto (ETM) et les permis A1 / A2. Sans ASSR2 ou équivalent (ASR), impossible de s'inscrire.",
      to: '/formation',
      toLabel: 'Découvrir les permis moto',
    },
    guideArticle: {
      to: '/magazine/assr2-preparer-3eme',
      label: 'Lire notre guide complet ASSR 2',
    },
    officialUrl: 'https://e-assr.education-securite-routiere.fr/preparer/assr/2/ASSR2',
  },
  {
    code: 'ASR',
    fullName: 'Attestation de Sécurité Routière',
    grade: 'Adultes',
    age: 'À partir de 16 ans',
    color: '#cf2031', // rouge
    icon: Users,
    audience: "Adultes sans ASSR (déscolarisés ou nés avant 1988)",
    prereq: "Passée dans un GRETA (organisme public de formation)",
    nextStep: "Équivalente à l'ASSR2 — permet l'inscription au permis",
    description:
      "Destinée aux personnes qui n'ont pas obtenu l'ASSR au collège (scolarité à l'étranger, déscolarisation précoce…). Mêmes exigences que l'ASSR2, passage en session unique dans un GRETA.",
    topics: [
      'Mêmes thèmes que ASSR2',
      'Inscription auprès d\'un GRETA (groupement public de formation)',
      "Examen gratuit — une seule tentative par session",
      'Résultat immédiat, attestation papier remise sur place',
    ],
    motoLink: {
      icon: Target,
      label: 'Pour les candidats adultes au permis',
      text: "Tu veux passer ton permis moto mais tu n'as ni ASSR1 ni ASSR2 ? L'ASR est la voie officielle. Contacte le GRETA de ton département.",
      to: null,
    },
    officialUrl: 'https://e-assr.education-securite-routiere.fr/preparer/assr/4/ASR',
  },
  {
    code: 'AER',
    fullName: "Attestation d'Éducation à la Route",
    grade: 'Établissements spécialisés',
    age: 'Public spécifique',
    color: '#7c3aed', // violet
    icon: Accessibility,
    audience: "Élèves en établissements médico-éducatifs (IME, ULIS, SEGPA adaptée…)",
    prereq: "Version adaptée aux élèves à besoins éducatifs particuliers",
    nextStep: "Équivalente à l'ASSR1 pour l'accès au BSR / permis AM",
    description:
      "Conçue pour les élèves en situation de handicap ou en établissement spécialisé. Contenu adapté et évaluation individualisée. Reconnue officiellement comme équivalent ASSR1.",
    topics: [
      'Contenus et supports adaptés',
      "Évaluation progressive, à rythme individuel",
      'Reconnaissance officielle équivalente à ASSR1',
      "Utilisable pour l'inscription au permis AM (14 ans)",
    ],
    motoLink: {
      icon: ShieldCheck,
      label: 'Inclusion et sécurité routière',
      text: "L'AER permet aux jeunes en situation de handicap d'accéder comme les autres à la mobilité autonome (AM, puis permis si conditions médicales réunies).",
      to: null,
    },
    officialUrl: 'https://e-assr.education-securite-routiere.fr/preparer/assr/3/AER',
  },
];

const AUDIENCES = [
  {
    title: 'Jeunes en formation',
    icon: GraduationCap,
    color: '#0ea5e9',
    who: 'Collégiens, lycéens, apprentis',
    why: "Passer le BSR / AM pour rouler en scooter 50 dès 14 ans, ou passer le permis dès 15 ans (A1 en conduite accompagnée).",
    howWeHelp: "On contextualise chaque ASSR avec le parcours moto (AM → A1 → A2).",
  },
  {
    title: 'Parents & professeurs',
    icon: Users,
    color: '#16a34a',
    who: 'Familles, enseignants EMC, référents sécurité routière',
    why: "Accompagner les jeunes dans la préparation, comprendre l'enjeu pédagogique, connaître les équivalences.",
    howWeHelp: "On t'explique clairement qui doit passer quoi, à quel âge, et pourquoi c'est utile.",
  },
  {
    title: 'Adultes sans ASSR',
    icon: Target,
    color: '#cf2031',
    who: 'Personnes nées avant 1988, scolarité à l\'étranger, déscolarisation',
    why: "Besoin de l'ASR pour s'inscrire au permis (voiture, moto, poids lourd).",
    howWeHelp: "On te redirige vers le GRETA de ton département et on te prépare aux thèmes de l'examen.",
  },
];

// ---------- Composants ----------
const AttestationCard = ({ attestation, onGo }) => {
  const Icon = attestation.icon;
  const MotoIcon = attestation.motoLink?.icon;
  return (
    <article
      className="mc-card overflow-hidden flex flex-col h-full"
      data-testid={`assr-card-${attestation.code.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {/* Header coloré */}
      <div
        className="p-5 sm:p-6 border-b border-border"
        style={{
          background: `linear-gradient(135deg, ${attestation.color}25 0%, ${attestation.color}05 100%)`,
        }}
      >
        <div className="flex items-start gap-4">
          <div
            className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center flex-shrink-0 text-white shadow-md"
            style={{ background: attestation.color }}
          >
            <Icon className="w-6 h-6 sm:w-7 sm:h-7" />
          </div>
          <div className="flex-1 min-w-0">
            <div
              className="text-[10px] font-black uppercase tracking-[0.16em]"
              style={{ color: attestation.color }}
            >
              {attestation.grade} · {attestation.age}
            </div>
            <h3
              className="text-2xl sm:text-3xl font-black tracking-tight leading-tight mt-1"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              {attestation.code}
            </h3>
            <p className="text-xs sm:text-sm text-foreground/70 mt-1 leading-snug">
              {attestation.fullName}
            </p>
          </div>
        </div>
      </div>

      {/* Corps */}
      <div className="p-5 sm:p-6 space-y-4 flex-1">
        {/* Audience + prérequis */}
        <div className="space-y-2">
          <div className="flex items-start gap-2 text-sm">
            <Users className="w-4 h-4 flex-shrink-0 mt-0.5 text-foreground/55" />
            <div>
              <strong className="text-foreground">Pour qui :</strong>{' '}
              <span className="text-foreground/80">{attestation.audience}</span>
            </div>
          </div>
          <div className="flex items-start gap-2 text-sm">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5 text-foreground/55" />
            <div>
              <strong className="text-foreground">Où :</strong>{' '}
              <span className="text-foreground/80">{attestation.prereq}</span>
            </div>
          </div>
          <div className="flex items-start gap-2 text-sm">
            <Award
              className="w-4 h-4 flex-shrink-0 mt-0.5"
              style={{ color: attestation.color }}
            />
            <div>
              <strong className="text-foreground">À quoi ça sert :</strong>{' '}
              <span className="text-foreground/80">{attestation.nextStep}</span>
            </div>
          </div>
        </div>

        <p className="text-sm text-foreground/80 leading-relaxed">
          {attestation.description}
        </p>

        {/* Thèmes abordés */}
        <div className="rounded-xl border border-border bg-muted/30 p-4">
          <div
            className="text-[10px] font-black uppercase tracking-[0.16em] mb-2"
            style={{ color: attestation.color }}
          >
            Ce qui est évalué
          </div>
          <ul className="space-y-1.5">
            {attestation.topics.map((t, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <CheckCircle2
                  className="w-3.5 h-3.5 flex-shrink-0 mt-1"
                  style={{ color: attestation.color }}
                />
                <span className="text-foreground/85">{t}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Lien pédagogique vers notre écosystème */}
        {attestation.motoLink && (
          <div
            className="rounded-xl p-4 border-l-4"
            style={{
              borderLeftColor: attestation.color,
              background: `${attestation.color}0d`,
            }}
          >
            <div
              className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.16em] mb-1"
              style={{ color: attestation.color }}
            >
              {MotoIcon && <MotoIcon className="w-3 h-3" />}
              <span>{attestation.motoLink.label}</span>
            </div>
            <p className="text-sm text-foreground/85 leading-relaxed">
              {attestation.motoLink.text}
            </p>
            {attestation.motoLink.to && (
              <button
                type="button"
                onClick={() => onGo(attestation.motoLink.to)}
                className="mt-2 inline-flex items-center gap-1 text-xs font-bold hover:underline"
                style={{ color: attestation.color }}
                data-testid={`assr-card-moto-link-${attestation.code.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {attestation.motoLink.toLabel}
                <ArrowRight className="w-3 h-3" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* CTA officiel */}
      <div className="p-5 sm:p-6 border-t border-border bg-muted/20 space-y-2.5">
        {attestation.guideArticle && (
          <button
            type="button"
            onClick={() => onGo(attestation.guideArticle.to)}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full border-2 px-4 py-2.5 text-sm font-bold transition-colors hover:bg-muted/50"
            style={{
              borderColor: attestation.color,
              color: attestation.color,
            }}
            data-testid={`assr-card-guide-${attestation.code.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <BookOpen className="w-4 h-4" />
            <span>{attestation.guideArticle.label}</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
        <a
          href={attestation.officialUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mc-btn-primary w-full inline-flex items-center justify-center gap-2"
          style={{ background: attestation.color }}
          data-testid={`assr-card-official-${attestation.code.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <BookOpen className="w-4 h-4" />
          <span>S'entraîner — site officiel</span>
          <ExternalLink className="w-3.5 h-3.5" />
        </a>
        <p className="text-[10px] text-foreground/55 text-center leading-snug">
          e-assr.education-securite-routiere.fr — Ministère de l'Éducation nationale
        </p>
      </div>
    </article>
  );
};

// ---------- Page ----------
export default function ASSR() {
  const navigate = useNavigate();

  useSeo({
    title: 'ASSR 1 et 2, ASR, AER — Préparer les attestations de sécurité routière',
    description:
      "ASSR1, ASSR2, ASR, AER : le guide ECSR des attestations officielles de sécurité routière. Pour qui, comment se préparer, lien avec le BSR / AM et le permis moto. Entraînement officiel gratuit.",
    keywords:
      'ASSR, ASSR1, ASSR2, ASR, AER, attestation scolaire sécurité routière, BSR, permis AM, permis moto, GRETA, éducation sécurité routière, e-ASSR',
    path: '/assr',
    jsonLd: [
      buildBreadcrumb([['ASSR / ASR / AER', '/assr']]),
      {
        '@context': 'https://schema.org',
        '@type': 'EducationalOccupationalProgram',
        name: 'Préparation ASSR / ASR / AER',
        description:
          "Guide de préparation aux attestations scolaires et adultes de sécurité routière, en lien avec le BSR / AM et le permis moto.",
        provider: {
          '@type': 'Organization',
          name: 'CODE 2 ROUES',
          url: 'https://code2roues.fr',
        },
        educationalCredentialAwarded: ['ASSR 1', 'ASSR 2', 'ASR', 'AER'],
      },
    ],
  });

  return (
    <div className="space-y-8 sm:space-y-14" data-testid="assr-page">
      {/* HERO ÉDITORIAL */}
      <section
        className="relative overflow-hidden rounded-sm editorial-border-double"
        data-testid="assr-hero"
      >
        <div
          className="relative p-6 sm:p-12 lg:p-16 min-h-[320px] sm:min-h-[420px] flex items-end"
          style={{
            background:
              'linear-gradient(135deg, hsl(var(--primary) / 0.15) 0%, hsl(var(--primary) / 0.02) 60%, transparent 100%)',
          }}
        >
          {/* décor cercles éditoriaux */}
          <div
            className="absolute -top-20 -right-20 w-80 h-80 rounded-full blur-3xl opacity-30"
            style={{ background: 'hsl(var(--primary))' }}
          />
          <div
            className="absolute bottom-0 left-0 w-full h-1"
            style={{ background: 'hsl(var(--primary))' }}
          />

          <div className="relative max-w-4xl">
            <div className="editorial-tag editorial-tag-red mb-4">
              <GraduationCap className="w-3 h-3" />
              <span>Éducation à la sécurité routière — officiel</span>
            </div>
            <h1
              className="text-[2rem] sm:text-5xl lg:text-6xl font-extrabold leading-[1.08] sm:leading-[1.02] text-foreground tracking-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
              data-testid="assr-title"
            >
              ASSR, ASR, AER : les attestations officielles de sécurité routière
            </h1>
            <p
              className="text-base sm:text-xl text-foreground/80 mt-4 sm:mt-5 max-w-2xl leading-relaxed italic"
              style={{ fontFamily: "'Crimson Text', Georgia, serif" }}
            >
              Quatre attestations, un objectif commun : former des usagers responsables dès l'école.
              Le guide pédagogique ECSR pour comprendre à quoi elles servent, qui est concerné,
              et comment elles s'articulent avec le permis moto et le BSR / AM.
            </p>
            <div className="mt-5 sm:mt-6 flex flex-wrap gap-2">
              <a
                href="#attestations"
                className="mc-btn-primary inline-flex items-center gap-2"
                data-testid="assr-hero-cta-list"
              >
                Voir les 4 attestations
                <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="https://e-assr.education-securite-routiere.fr/preparer/assr"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm font-bold hover:bg-muted transition-colors"
                data-testid="assr-hero-cta-official"
              >
                <ExternalLink className="w-4 h-4" />
                S'entraîner sur le site officiel
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* BLOC "POUR QUI ?" */}
      <section data-testid="assr-audiences">
        <div className="flex items-end justify-between mb-5 gap-3 flex-wrap">
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
              Partie 1 — Pour qui ?
            </div>
            <h2
              className="text-2xl sm:text-3xl font-black tracking-tight mt-1"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              3 publics, un même enjeu
            </h2>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {AUDIENCES.map((a) => {
            const Icon = a.icon;
            return (
              <div
                key={a.title}
                className="mc-card p-5 sm:p-6 relative"
                data-testid={`assr-audience-${a.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <div
                  className="absolute top-0 left-0 bottom-0 w-1 rounded-l-2xl"
                  style={{ background: a.color }}
                />
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 text-white"
                  style={{ background: a.color }}
                >
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-black tracking-tight mb-1">{a.title}</h3>
                <p className="text-[11px] font-semibold text-foreground/55 uppercase tracking-wider mb-3">
                  {a.who}
                </p>
                <div className="space-y-2 text-sm text-foreground/80 leading-relaxed">
                  <p>
                    <strong className="text-foreground">Pourquoi :</strong> {a.why}
                  </p>
                  <p>
                    <strong style={{ color: a.color }}>Notre valeur ajoutée :</strong>{' '}
                    {a.howWeHelp}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* LES 4 ATTESTATIONS */}
      <section id="attestations" data-testid="assr-attestations">
        <div className="flex items-end justify-between mb-5 gap-3 flex-wrap">
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
              Partie 2 — Les 4 attestations
            </div>
            <h2
              className="text-2xl sm:text-3xl font-black tracking-tight mt-1"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Repère ton attestation
            </h2>
            <p className="text-sm text-foreground/65 mt-2 max-w-2xl leading-relaxed">
              Chaque attestation a son public et son contenu. L'entraînement se fait
              gratuitement sur le site officiel du Ministère. Nous, on t'aide à comprendre
              <strong> laquelle te concerne</strong> et à quoi elle sert dans ton parcours.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5 sm:gap-6">
          {ATTESTATIONS.map((a) => (
            <AttestationCard key={a.code} attestation={a} onGo={navigate} />
          ))}
        </div>
      </section>

      {/* ARTICULATION AVEC LE PARCOURS CODE 2 ROUES */}
      <section className="mc-card p-5 sm:p-7 relative overflow-hidden" data-testid="assr-parcours">
        <div
          className="absolute -top-10 -right-10 w-52 h-52 rounded-full blur-3xl opacity-20"
          style={{ background: 'hsl(var(--primary))' }}
        />
        <div className="relative">
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))] mb-2">
            Partie 3 — Articulation parcours moto
          </div>
          <h2
            className="text-2xl sm:text-3xl font-black tracking-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Comment ces attestations s'articulent avec ton permis
          </h2>

          <div className="grid md:grid-cols-2 gap-4 mt-5">
            <div
              className="rounded-2xl p-5 border-l-4"
              style={{ borderLeftColor: '#0ea5e9', background: '#0ea5e90d' }}
            >
              <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.14em] text-[#0ea5e9] mb-2">
                <MapPin className="w-3.5 h-3.5" /> Parcours jeune
              </div>
              <p className="text-sm text-foreground/85 leading-relaxed">
                <strong>5ᵉ — ASSR1</strong> → <strong>14 ans — Permis AM</strong> (BSR, scooter
                50 cm³) → <strong>3ᵉ — ASSR2</strong> → <strong>16 ans — Permis A1</strong> (moto
                125 cm³) ou <strong>18 ans — Permis A2</strong>.
              </p>
            </div>

            <div
              className="rounded-2xl p-5 border-l-4"
              style={{ borderLeftColor: '#cf2031', background: '#cf20310d' }}
            >
              <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.14em] text-[#cf2031] mb-2">
                <Target className="w-3.5 h-3.5" /> Parcours adulte
              </div>
              <p className="text-sm text-foreground/85 leading-relaxed">
                Pas d'ASSR au collège ? <strong>ASR</strong> dans un GRETA →{' '}
                <strong>Code moto (ETM)</strong> sur CODE 2 ROUES →{' '}
                <strong>Formation plateau + circulation</strong> en moto-école.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-5">
            <Button onClick={() => navigate('/parcours')} className="mc-btn-primary" data-testid="assr-cta-parcours">
              Voir tous les parcours <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/revision')}
              data-testid="assr-cta-revision"
            >
              Démarrer la révision Code moto
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ — questions fréquentes */}
      <section data-testid="assr-faq">
        <div className="mb-5">
          <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
            Partie 4 — FAQ
          </div>
          <h2
            className="text-2xl sm:text-3xl font-black tracking-tight mt-1"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Les questions les plus posées
          </h2>
        </div>

        <div className="space-y-3">
          {[
            {
              q: "J'ai perdu mon attestation ASSR : comment en obtenir un duplicata ?",
              a: "Contacte le secrétariat du collège où tu l'as passée (même si tu n'y es plus scolarisé). Ils conservent les archives. Si l'établissement a fermé, tu peux solliciter le rectorat de l'académie. En dernier recours, passer l'ASR dans un GRETA reste la solution rapide.",
            },
            {
              q: "L'ASSR2 est-elle valable à vie ?",
              a: "Oui. Une fois obtenue, l'ASSR2 (ou l'ASR) reste valable toute ta vie. Elle est demandée une seule fois, au moment de la première inscription à un permis (B, A1, A2, BE…).",
            },
            {
              q: "Je suis né avant 1988. Dois-je quand même passer l'ASSR2 ou l'ASR ?",
              a: "Non. L'obligation d'ASSR2 ou ASR ne s'applique qu'aux candidats nés après le 31 décembre 1987 qui s'inscrivent à un permis pour la première fois.",
            },
            {
              q: "L'entraînement officiel est-il vraiment gratuit ?",
              a: "Oui, 100%. Le site e-assr.education-securite-routiere.fr est édité par le Ministère de l'Éducation nationale. Pas d'inscription, pas d'abonnement, pas de limite d'essais.",
            },
            {
              q: 'Quelle différence entre ASSR et Code moto (ETM) ?',
              a: "L'ASSR (passée au collège) est une sensibilisation générale à la sécurité routière. L'ETM (Épreuve Théorique Moto) est un examen technique spécifique au permis moto, avec 40 questions ciblées sur la conduite deux-roues. Tu as besoin des DEUX pour passer le permis moto.",
            },
          ].map((item, i) => (
            <details
              key={i}
              className="mc-card p-4 sm:p-5 group"
              data-testid={`assr-faq-${i}`}
            >
              <summary className="cursor-pointer font-bold text-sm sm:text-base flex items-start gap-3 list-none">
                <Lightbulb className="w-4 h-4 flex-shrink-0 mt-0.5 text-[hsl(var(--primary))]" />
                <span className="flex-1">{item.q}</span>
                <span className="text-[hsl(var(--primary))] text-lg leading-none group-open:rotate-45 transition-transform">
                  +
                </span>
              </summary>
              <p className="text-sm text-foreground/80 leading-relaxed mt-3 pl-7">{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* DISCLAIMER SOURCE OFFICIELLE */}
      <footer
        className="mc-card p-4 sm:p-5 text-xs sm:text-sm text-foreground/65 flex items-start gap-3"
        data-testid="assr-source"
      >
        <FileText className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <p>
          <strong className="not-italic text-foreground">Source officielle :</strong>{' '}
          L'entraînement et la documentation de référence sont fournis par le Ministère de
          l'Éducation nationale via le site{' '}
          <a
            href="https://e-assr.education-securite-routiere.fr"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-foreground"
          >
            e-assr.education-securite-routiere.fr
          </a>
          . Cette page a un but purement pédagogique et de contextualisation. CODE 2 ROUES n'est
          ni l'éditeur ni le certificateur des ASSR / ASR / AER — nous sommes un outil
          d'accompagnement conçu par des moniteurs ECSR.
        </p>
      </footer>
    </div>
  );
}
